import { MOBILE_CSS_FONTS, MOBILE_DEFAULT_FONT } from '../../../../foreground/fonts';
import type { Font } from '../../../../types';
import { LayoutMode } from '../../../../types';
import { Theme } from '../../../css/baseStyles';
import { generateAssetFontCss } from '../../../utils/getAssetFontCss';
import getWebviewHorizontalMarginCss, { getWebviewHorizontalMarginValue } from '../../../utils/getWebviewHorizontalMarginCss';
import { getBaseStyles } from './baseStyles';
import { getDigestStyles, getPDFPreviewStyles } from './digestStyles';
import { getEpubStyles } from './epubStyles';
import { getHeaderStyles } from './headerStyles';
import { getHorizontalPaginationStyles } from './horizontalPaginationStyles';
import { getOriginalEmailStyles } from './originalEmailStyles';
import { getTTSStyles } from './ttsStyles';
import { getVerticalPaginationStyles } from './verticalPaginationStyles';

export type WebviewStyleProps = {
  deviceWidth: number;
  font: Font;
  fontSize: number;
  horizontalMargin: number;
  isHighContrast: boolean;
  isRtl: boolean;
  areEpubOriginalStylesEnabled: boolean;
  shouldJustifyText: boolean;
  lineHeight: number;
  marginTop: number;
  deviceHeight: number;
  isDigestView: boolean;
  isEmailOriginalView: boolean;
  layoutMode: LayoutMode;
  isPDFDigestPreview: boolean;
  isYoutubeVideo: boolean;
  theme: Theme;
  isTablet: boolean;
  platformOS: 'ios' | 'android' | 'windows' | 'macos' | 'web';
  isEinkMode: boolean;
  isChunked?: boolean;
};

export const getWebviewInnerStyle = ({
  deviceWidth,
  deviceHeight,
  isDigestView,
  isEmailOriginalView,
  layoutMode,
  isPDFDigestPreview,
  isYoutubeVideo,
  font,
  areEpubOriginalStylesEnabled,
  marginTop,
  isHighContrast,
  horizontalMargin,
  fontSize,
  lineHeight,
  shouldJustifyText,
  theme,
  platformOS,
  isTablet,
  isEinkMode,
  isChunked = false,
}: WebviewStyleProps) => {
  const { styleContent: fontFamilyStyles, links: fontLinks } = generateAssetFontCss({
    fontName: font,
    cssFontName: font ? (MOBILE_CSS_FONTS as { [key: string]: string; })[font] : (MOBILE_CSS_FONTS as { [key: string]: string; })[MOBILE_DEFAULT_FONT],
    platformOS,
  });

  return `
  ${areEpubOriginalStylesEnabled ? getEpubStyles(shouldJustifyText) : ''}
  <style id="RWMarginStyle">
    ${getWebviewHorizontalMarginCss(horizontalMargin, isTablet).css}
  </style>
  <style id="RWFontFamilyStyle" font-name="${font}">
    ${fontFamilyStyles}
  </style>
  ${fontLinks.join(' ')}
  <style>
    :root {
      --reading-editable-font-size: ${fontSize};
      --reading-editable-line-height: ${lineHeight};
      --reading-editable-text-justify: ${shouldJustifyText ? 'justify' : 'start'};
      --reading-text-content-width: ${deviceWidth - getWebviewHorizontalMarginValue(horizontalMargin, isTablet) * 2}px;
    }
    body {
    }

    #generatedStyles {
      display: none;
    }

    .display-none {
      display: none!important;
    }


    #readerCanvas {
      display: none;
    }

    .image-compare-canvas {
      display: none;
    }

    .debug-border {
      position: absolute;
      background: blue;
      display: none;
      height: 3px;
      width: 100%;
      z-index: 2000;
    }
    .debug-border-x {
      position: absolute;
      background: blue;
      display: none;
      height: 100%;
      width: 3px;
      z-index: 2000;
    }
  </style>
  ${getHeaderStyles({ theme, isYoutubeVideo, marginTop, isEinkMode })}
  ${getTTSStyles({ theme, isHighContrast })}
  ${getBaseStyles({
    theme,
    marginTop,
    isHighContrast,
    deviceHeight,
    layoutMode,
    isChunked,
  })}
  ${layoutMode === LayoutMode.VerticalPagination ? getVerticalPaginationStyles({ theme, deviceHeight }) : ''}
  ${layoutMode === LayoutMode.HorizontalPagination ? getHorizontalPaginationStyles({ theme, deviceHeight }) : ''}
  ${
    platformOS !== 'ios'
      ? `<style>
      /* hack to prevent hyphens from breaking android text selection */
      .document-content {
        hyphens: initial!important;
      }
    </style>
  `
      : `<style></style>`
  }
  ${isDigestView ? getDigestStyles(horizontalMargin, isTablet) : ''}
  ${isPDFDigestPreview ? getPDFPreviewStyles(theme) : ''}
  ${isEmailOriginalView ? getOriginalEmailStyles(deviceWidth) : ''}
`;
};
